import { Analytics } from '@cobliteam/analytics';
import type { Preferences, UserRole } from '@cobliteam/common-types';
import { useLocalStorage, useUser } from '@cobliteam/hooks';
import { useTranslation } from '@cobliteam/i18n';
import { Alert, Dialog } from '@cobliteam/ui/components';
import { isSameDay } from '@cobliteam/utils/date';
import { Widget } from '@typeform/embed-react';
import { useCallback, useEffect, useState } from 'react';

interface DialogTypeformSurveyProps {
  title?: string;
  typeformId: string;
  open?: boolean;
  onClose?: () => void;
  /** Number of days to display the survey with user rejection (close without answer). Default: 1 */
  maxDaysRejectionCount?: number;
  enabledRoles?: UserRole[];
  shouldOpen?: (needOpen: boolean) => void;
}

/**
 * This component renders the Typeform dialog. It will appear at home page and once the user closes it, it will not appear again.
 *
 * CAUTION: Only one typeform can be active at a time, both for user experience (not having multiple popping surveys) and
 *          due to shared preferences state. Before using it make sure no other team is running a survey.
 */
const DialogTypeformSurvey = ({ typeformId, open, onClose, enabledRoles, maxDaysRejectionCount = 1, shouldOpen, ...props }: DialogTypeformSurveyProps) => {
  const { t } = useTranslation('common');
  const { title = t('typeform.survey_form') } = props;
  const [openState, setOpenState] = useState(open);
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');
  const { user, session } = useUser();

  const updateFormTracking = (answered: boolean) => {
    const typeformSurvey = preferences?.typeformSurvey;
    const hasCurrentFormStored = typeformSurvey?.typeformId === typeformId;

    const timesSeen = hasCurrentFormStored ? typeformSurvey?.timesSeen || 0 : 0;
    const lastSeenAt = new Date();

    setPreferences({
      ...preferences,
      typeformSurvey: {
        typeformId,
        lastSeenAt,
        timesSeen: timesSeen + 1,
        answered,
      },
    });
  };

  const handleClose = () => {
    Analytics.track('typeform-survey - close', {
      typeformId,
    });
    updateFormTracking(false);
    setOpenState(false);
    onClose?.();
  };

  const handleSubmit = () => {
    Analytics.track('typeform-survey - submit', {
      typeformId,
    });

    Alert.success(t('typeform.thank_you_alert'));
    updateFormTracking(true);
    setOpenState(false);
    onClose?.();
  };

  const shouldOpenForm = useCallback((): boolean => {
    if (user && enabledRoles && !enabledRoles.includes(user.role)) return false;

    const typeformSurvey = preferences?.typeformSurvey;

    // If the form is already answered, do not show it again
    if (typeformSurvey?.typeformId === typeformId) {
      const { answered, timesSeen, lastSeenAt } = typeformSurvey;

      // If the form is already answered, do not show it again
      if (answered) return false;

      // If the form is already seen more than maxDaysRejectionCount times, do not show it again
      if (timesSeen === maxDaysRejectionCount) return false;

      // If the form is already seen today, do not show it again
      if (isSameDay(new Date(), lastSeenAt)) return false;

      return true;
    }

    return true;
  }, [preferences, user]);

  useEffect(() => {
    const needOpen = shouldOpenForm();
    if (shouldOpen) shouldOpen(needOpen);
  }, [shouldOpenForm, shouldOpen]);

  useEffect(() => {
    if (open) {
      setOpenState(true);
    }
  }, [open]);

  const hiddenFields = {
    url: window.location.href || '',
    page_route: window.location.pathname || '',
    user_name: user?.name || '',
    user_email: user?.email || '',
    user_id: user?.id || '',
    fleet_id: session?.fleet.id || '',
    fleet_name: session?.fleet.name || '',
  };

  return (
    <Dialog open={openState}>
      <Dialog.Content size='lg'>
        <Dialog.Header className='flex justify-between'>
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description className='sr-only' />
          <Dialog.Close onClick={handleClose} />
        </Dialog.Header>
        <Widget id={typeformId} className='h-[500px]' onClose={handleClose} onSubmit={handleSubmit} hidden={hiddenFields} />
      </Dialog.Content>
    </Dialog>
  );
};

export default DialogTypeformSurvey;
